import { ReactNode, MouseEvent, HTMLAttributes } from 'react';
import styled from 'styled-components';
import {
  color,
  ColorProps,
  BackgroundColorProps,
  variant,
  buttonStyle,
  ButtonStyleProps,
  margin,
  MarginProps,
  textAlign,
  TextAlignProps,
  display,
  DisplayProps,
  flexbox,
  FlexboxProps,
  height,
  HeightProps,
  width,
  WidthProps,
  border,
  BorderProps,
} from 'styled-system';
import { AnchorModel as Model } from './AnchorModel';
import { Link } from './Link';
import { showModal } from '../modal/modalShow';
import { ModalMessage } from '../modal/ModalMessage';
import { ModalEnlaceExterno } from '../modal/ModalEnlacesExternos';
import React from 'react';
import { keyCodes } from '../../constants/keycodes';

export type AnchorProps = {
  children: ReactNode;
  onClick?: (event: MouseEvent<HTMLElement>) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLElement>) => void;
  onMouseOver?: (event: React.MouseEvent<HTMLElement>) => void;
  onMouseOut?: (event: React.MouseEvent<HTMLElement>) => void;
  data?: Model;
  tabIndex?: number;
  title?: string;
} & HeightProps &
  WidthProps &
  ButtonStyleProps &
  MarginProps &
  TextAlignProps &
  DisplayProps &
  FlexboxProps &
  BorderProps &
  BackgroundColorProps;

export const Anchor = React.forwardRef<HTMLAnchorElement, AnchorProps>((props: AnchorProps, ref) => {
  const { children, onClick, onKeyDown, onMouseOver, onMouseOut, data, tabIndex, title, ...otherProps } = props;

  const onClickEvent = async (event: MouseEvent<HTMLElement>) => {
    if (props == null) return;

    event.preventDefault();

    if (onClick) onClick(event);

    await processSelection();
  };
  const onKeyDownEvent = async (event: React.KeyboardEvent<HTMLElement>) => {
    if (props == null) return;

    if (onKeyDown) onKeyDown(event);

    if (event.key === keyCodes.Enter) {
      event.preventDefault();
      await processSelection();
    }
  };

  const processSelection = async () => {
    if (data?.containsMessage()) {
      await openMessagePopup(data);
      return;
    }
    if (!data?.containsLink()) {
      return;
    }

    const link: Link | undefined = data?.enlace;

    if (link?.isExterno) await openExternalPopup(link);
    else {
      if (link?.abreSolapa) await openNewTab(link?.ruta);
      else await navigateToUrl(link?.ruta);
    }
  };
  /**Abre un popup en el que se muestra el mensaje */
  const openMessagePopup = async (data?: Model) => {
    if (data) showModal({ entity: data, ModalComponent: ModalMessage });
  };
  /** Abre popup de aviso de salida del sitio (link externo) */
  const openExternalPopup = async (link: Link) => {
    if (link?.ruta) showModal({ url: link.ruta, ModalComponent: ModalEnlaceExterno });
  };

  const Container: React.ElementType<ContainerProps> = data?.containsLink() ? StyledAnchor : StyledContainer;
  const isClickable = data?.containsLink() || data?.containsMessage() || onClick != null;

  return (
    <Container
      href={data?.getUrl()}
      id={data?.getTag()}
      tabIndex={tabIndex}
      onClick={onClickEvent}
      onKeyDown={onKeyDownEvent}
      onMouseOver={onMouseOver}
      onMouseOut={onMouseOut}
      ref={ref}
      title={title}
      aria-label={title}
      isClickable={isClickable}
      {...otherProps}
    >
      {props.children}
    </Container>
  );
});

Anchor.defaultProps = {
  tabIndex: 0,
};

type ContainerProps = {
  isClickable: boolean;
  href?: string | undefined;
  ref?: ((instance: HTMLAnchorElement | null) => void) | React.RefObject<HTMLAnchorElement> | null | undefined;
} & HTMLAttributes<HTMLElement> &
  ColorProps &
  ButtonStyleProps &
  MarginProps &
  TextAlignProps &
  DisplayProps &
  FlexboxProps &
  HeightProps &
  WidthProps;

const ContainerStyledFunctions = [
  color,
  buttonStyle,
  margin,
  textAlign,
  display,
  flexbox,
  height,
  width,
  border,
  variant({
    variants: {
      transparent: {
        '&:hover': {
          opacity: 0.5,
        },
      },
    },
  }),
];

const StyledContainer = styled.div<ContainerProps>(
  (props) => ({
    cursor: props.isClickable ? 'pointer' : 'default',
  }),
  ...ContainerStyledFunctions
);

const StyledAnchor = styled.a<ContainerProps>(
  {
    color: 'inherit',
    'text-decoration': 'none',
    cursor: 'pointer',
  },
  ...ContainerStyledFunctions
);

/** Abre url en una pestaña nueva */
const openNewTab = async (url: string | undefined) => {
  if (url) window.open(url, '_blank');
};
/** Abre url en la pestaña actual */
const navigateToUrl = async (url: string | undefined) => {
  if (url) window.location.href = url;
};
